import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import axiosInstance from "../Utils/AxoisClient";
import { useAuth } from "./UserAuthContext";
import loginBg from "../Assets/LoginBG.png";
import loginBgFirst from "../Assets/loginbgfirst.png";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";

const Login = () => {
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const { login, logout } = useAuth();
  const [snackBar, setSnackBar] = useState(false);
  const navigate = useNavigate();
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  /** ----------------- LOGIN ----------------- **/
  const handleLogin = async () => {
    try {
      const response = await axiosInstance.post("company/login", {
        email,
        password,
      });
      if (response.status === 200) {
        Cookies.set("_auth", response.data.ctoken, { expires: 1 });
        login();
        navigate("/");
      } else {
        logout();
        setSnackBar(true);
        setAlertSeverity("error");
        toast.error("Login failed. Please check your credentials.", {
          autoClose: 2000,
        });
        setAlertMessage("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      toast.error(error?.response?.data?.message, { autoClose: 2000 });
      setAlertMessage(error?.response?.data?.message || "Login failed.");
    }
  };

  useEffect(() => {
    if (type === "demo") {
      setEmail("demo@gmail.com");
      setPassword("demo@321");
    }
  }, [type]);

  useEffect(() => {
    if (email && password && type === "demo") {
      handleLogin();
    }
  }, [email, password]);

  /** ----------------- SEND OTP ----------------- **/
  const handleSendOtp = async () => {
    try {
      const response = await axiosInstance.post("company/forgot-password", {
        email,
      });
      if (response.status === 200) {
        setSnackBar(true);
        setAlertSeverity("success");
        toast.success("OTP sent to your email.", { autoClose: 2000 });
        setAlertMessage("OTP sent to your email.");
        setStep(2);
        setTimer(120); // 2 min countdown
      } else {
        toast.error("Failed to send OTP.", { autoClose: 2000 });
        setAlertSeverity("error");
        setSnackBar(true);
        setAlertMessage("Failed to send OTP.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      setAlertMessage(error?.response?.data?.message || "Failed to send OTP.");
      toast.error(error?.response?.data?.message || "Failed to send OTP.", {
        autoClose: 2000,
      });
    }
  };

  /** ----------------- VERIFY OTP ----------------- **/
  const handleVerifyOtp = async () => {
    try {
      const response = await axiosInstance.post("company/verify-otp", {
        email,
        otp,
      });
      if (response.status === 200) {
        setSnackBar(true);
        setAlertSeverity("success");
        toast.success("OTP verified.", { autoClose: 2000 });
        setAlertMessage("OTP verified.");
        setStep(3);
      } else {
        setSnackBar(true);
        toast.error("Invalid OTP.", { autoClose: 2000 });
        setAlertSeverity("error");
        setAlertMessage("Invalid OTP.");
      }
    } catch (error) {
      setSnackBar(true);
      toast.error("OTP verification failed.", { autoClose: 2000 });
      setAlertSeverity("error");
      setAlertMessage(error?.response?.data?.message || "Invalid OTP.");
    }
  };

  /** ----------------- RESET PASSWORD ----------------- **/
  const handleResetPassword = async () => {
    if (password !== confirmPassword) {
      setAlertSeverity("error");
      toast.error("Passwords do not match.", { autoClose: 2000 });
      setAlertMessage("Passwords do not match.");
      return;
    }
    try {
      const response = await axiosInstance.post("company/update-password", {
        email,
        confirm_password: confirmPassword,
        password,
      });
      if (response.status === 200) {
        setSnackBar(true);
        toast.success("Password reset successful. Please login.", {
          autoClose: 2000,
        });
        setAlertSeverity("success");
        setAlertMessage("Password reset successful. Please login.");
        setMode("login");
        setStep(1);
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setOtp("");
      } else {
        setSnackBar(true);
        toast.error("Failed to reset password.", { autoClose: 2000 });
        setAlertSeverity("error");
        setAlertMessage("Failed to reset password.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      toast.error("Failed to reset password.", { autoClose: 2000 });
      setAlertMessage(
        'error?.response?.data?.message || "Failed to reset password."'
      );
    }
  };

  /** ----------------- TIMER FOR RESEND OTP ----------------- **/
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  /** ----------------- BUTTON STYLE ----------------- **/
  const btnStyle = {
    background: "linear-gradient(90deg, #7b42ff, #6435ff)",
    textTransform: "none",
    fontWeight: "bold",
    py: 1.2,
    borderRadius: "8px",
    "&:hover": {
      background: "linear-gradient(90deg, #6a38e0, #562ad4)",
    },
  };

  /** ----------------- RENDER FORGOT PASSWORD FLOW ----------------- **/
  const renderForgotFlow = () => {
    if (step === 1) {
      // Step 1: Enter email
      return (
        <>
          <TextField
            placeholder="Enter your email"
            fullWidth
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleSendOtp}
          >
            Send OTP
          </Button>
          <Link
            component="button"
            variant="body2"
            sx={{ mt: 2 }}
            onClick={() => {
              setMode("login");
              setStep(1);
            }}
          >
            Back to login
          </Link>
        </>
      );
    }

    if (step === 2) {
      // Step 2: Verify OTP
      return (
        <>
          <TextField
            placeholder="Enter OTP"
            fullWidth
            variant="outlined"
            margin="normal"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleVerifyOtp}
          >
            Verify OTP
          </Button>
          <Button
            variant="text"
            fullWidth
            disabled={timer > 0}
            onClick={handleSendOtp}
            sx={{ mt: 1 }}
          >
            {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
          </Button>
        </>
      );
    }

    if (step === 3) {
      // Step 3: New password
      return (
        <>
          <TextField
            placeholder="New password"
            type="password"
            fullWidth
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            placeholder="Confirm password"
            type="password"
            fullWidth
            variant="outlined"
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleResetPassword}
          >
            Save Password
          </Button>
        </>
      );
    }
  };

  return (
    // <Box
    //   sx={{
    //     height: "100vh",
    //     display: "flex",
    //     alignItems: "center",
    //     justifyContent: "center",
    //     // backgroundColor: "#f5f5f5",
    //     backgroundImage: `url(${loginBg})`,
    //     backgroundSize: "cover",
    //     backgroundPosition: "center",
    //   }}
    // >
    //   <Box
    //     sx={{
    //       display: "flex",
    //       width: "80%",
    //       height: "80%",
    //       borderRadius: "16px",
    //       overflow: "hidden",
    //       boxShadow: 4,
    //       backgroundColor: "#fff",
    //       padding: 4,
    //     }}
    //   >
    //     {/* Left Section */}

    //     <Box
    //       sx={{
    //         flex: 1,
    //         // background: "linear-gradient(135deg, #3b0ca5, #1d0735)",
    //         background: "transparent",
    //         color: "#fff",
    //         display: "flex",
    //         alignItems: "center",
    //         borderRadius: 2,
    //         backgroundImage: `url(${loginBg})`,
    //         backgroundSize: "cover",
    //         // backgroundPosition: "right",
    //         backgroundPosition: "center 20%",
    //       }}
    //     >
    //       <Typography
    //         variant="h4"
    //         sx={{
    //           fontWeight: "bold",
    //           alignItems: "center",
    //           justifyContent: "flex-start",
    //           lineHeight: 1.3,
    //           p: 3,
    //         }}
    //       >
    //         Your One Stop <br /> Procurement Solution
    //       </Typography>
    //     </Box>

    //     {/* Right Section */}
    //     <Box
    //       sx={{
    //         flex: 1,
    //         p: 4,
    //         display: "flex",
    //         flexDirection: "column",
    //         justifyContent: "center",
    //         position: "relative",
    //         backgroundColor: "#fff",
    //       }}
    //     >
    //       {/* Logo */}
    //       <Box sx={{ position: "absolute", top: 20, right: 20 }}>
    //         <img
    //           src="https://360.procurementresource.com/assets/images/prlogo.webp"
    //           alt="Logo"
    //           style={{ width: "140px" }}
    //         />
    //       </Box>

    //       <Typography
    //         variant="h4"
    //         gutterBottom
    //         sx={{
    //           fontFamily: "'Lora', serif",
    //           fontWeight: 600,
    //           fontSize: "35px",
    //           lineHeight: 1.3,
    //         }}
    //       >
    //         {mode === "login" ? "Get Started" : "Reset Password"}
    //       </Typography>
    //       <Typography
    //         variant="body2"
    //         sx={{
    //           fontFamily: "'Inter', sans-serif",
    //           color: "text.secondary",
    //         }}
    //         mb={3}
    //       >
    //         {mode === "login"
    //           ? "Welcome to sourcing compass"
    //           : step === 1
    //           ? "Enter your email to receive OTP"
    //           : step === 2
    //           ? "Enter OTP sent to your email"
    //           : "Create a new password"}
    //       </Typography>

    //       {mode === "login" ? (
    //         <>
    //           <TextField
    //             placeholder="Your email"
    //             fullWidth
    //             variant="outlined"
    //             margin="normal"
    //             value={email}
    //             onChange={(e) => setEmail(e.target.value)}
    //           />
    //           <TextField
    //             placeholder="Your password"
    //             type="password"
    //             fullWidth
    //             variant="outlined"
    //             margin="normal"
    //             value={password}
    //             onChange={(e) => setPassword(e.target.value)}
    //           />
    //           <Link
    //             component="button"
    //             variant="body2"
    //             sx={{ alignSelf: "flex-end", color: "red", mb: 2 }}
    //             onClick={() => setMode("forgot")}
    //           >
    //             Forget password
    //           </Link>
    //           <Button
    //             variant="contained"
    //             fullWidth
    //             sx={btnStyle}
    //             onClick={handleLogin}
    //           >
    //             Login
    //           </Button>
    //         </>
    //       ) : (
    //         renderForgotFlow()
    //       )}
    //     </Box>
    //   </Box>
    // </Box>

    // <Box
    //   sx={{
    //     height: "100vh",
    //     display: "flex",
    //     alignItems: "center",
    //     justifyContent: "center",
    //     backgroundImage: `url(${loginBg})`,
    //     backgroundSize: "cover",
    //     backgroundPosition: "center",
    //   }}
    // >
    //   <Snackbar
    //     open={snackBar}
    //     anchorOrigin={{ vertical: "top", horizontal: "right" }}
    //     key={"top" + "right"}
    //     autoHideDuration={6000}
    //     onClose={() => setSnackBar(false)}
    //   >
    //     <Alert
    //       onClose={() => setSnackBar(false)}
    //       severity={alertSeverity}
    //       variant="filled"
    //       sx={{ width: "100%" }}
    //     >
    //       {alertMessage}
    //     </Alert>
    //   </Snackbar>
    //   <Box
    //     sx={{
    //       width: "80%",
    //       height: "80%",
    //       borderRadius: "16px",
    //       overflow: "hidden",
    //       boxShadow: 4,
    //       backgroundColor: "#fff",
    //       // background: "transparent",
    //       padding: 4,
    //     }}
    //   >
    //     <Grid container sx={{ height: "100%" }}>
    //       {/* Left Section */}
    //       <Grid
    //         item
    //         xs={12}
    //         md={6}
    //         sx={{
    //           background: "transparent",
    //           color: "#fff",
    //           display: "flex",
    //           alignItems: "center",
    //           borderRadius: 2,
    //           backgroundImage: `url(${loginBgFirst})`,
    //           backgroundSize: "cover",
    //           backgroundPosition: "center 20%",
    //         }}
    //       >
    //         <Typography
    //           variant="h4"
    //           sx={{
    //             fontWeight: "bold",
    //             alignItems: "center",
    //             justifyContent: "flex-start",
    //             lineHeight: 1.3,
    //             p: 3,
    //           }}
    //         >
    //           Your One Stop <br /> Procurement Solution
    //         </Typography>
    //       </Grid>

    //       {/* Right Section */}
    //       <Grid
    //         item
    //         xs={12}
    //         md={6}
    //         sx={{
    //           p: 4,
    //           display: "flex",
    //           flexDirection: "column",
    //           justifyContent: "center",
    //           position: "relative",
    //           backgroundColor: "#fff",
    //         }}
    //       >
    //         {/* Logo */}
    //         <Box sx={{ position: "absolute", top: 20, right: 20 }}>
    //           <img
    //             src="https://360.procurementresource.com/assets/images/prlogo.webp"
    //             alt="Logo"
    //             style={{ width: "140px" }}
    //           />
    //         </Box>

    //         <Typography
    //           variant="h4"
    //           gutterBottom
    //           sx={{
    //             fontFamily: "'Lora', serif",
    //             fontWeight: 600,
    //             fontSize: "35px",
    //             lineHeight: 1.3,
    //           }}
    //         >
    //           {mode === "login" ? "Get Started" : "Reset Password"}
    //         </Typography>
    //         <Typography
    //           variant="body2"
    //           sx={{
    //             fontFamily: "'Inter', sans-serif",
    //             color: "text.secondary",
    //           }}
    //           mb={3}
    //         >
    //           {mode === "login"
    //             ? "Welcome to sourcing compass"
    //             : step === 1
    //             ? "Enter your email to receive OTP"
    //             : step === 2
    //             ? "Enter OTP sent to your email"
    //             : "Create a new password"}
    //         </Typography>

    //         {mode === "login" ? (
    //           <>
    //             <TextField
    //               placeholder="Your email"
    //               fullWidth
    //               variant="outlined"
    //               margin="normal"
    //               value={email}
    //               onChange={(e) => setEmail(e.target.value)}
    //             />
    //             {/* <TextField
    //               placeholder="Your password"
    //               type="password"
    //               fullWidth
    //               variant="outlined"
    //               margin="normal"
    //               value={password}
    //               onChange={(e) => setPassword(e.target.value)}
    //             /> */}
    //             <TextField
    //               placeholder="Your password"
    //               type={showPassword ? "text" : "password"}
    //               fullWidth
    //               variant="outlined"
    //               margin="normal"
    //               value={password}
    //               onChange={(e) => setPassword(e.target.value)}
    //               sx={{ background: "#e8f0fe" }}
    //               InputProps={{
    //                 endAdornment: (
    //                   <InputAdornment position="end">
    //                     <IconButton
    //                       onClick={() => setShowPassword((prev) => !prev)}
    //                       edge="end"
    //                     >
    //                       {showPassword ? <VisibilityOff /> : <Visibility />}
    //                     </IconButton>
    //                   </InputAdornment>
    //                 ),
    //               }}
    //             />
    //             <Link
    //               component="button"
    //               variant="body2"
    //               sx={{ alignSelf: "flex-end", color: "red", mb: 2 }}
    //               onClick={() => setMode("forgot")}
    //             >
    //               Forget password
    //             </Link>
    //             <Button
    //               variant="contained"
    //               fullWidth
    //               sx={btnStyle}
    //               onClick={handleLogin}
    //             >
    //               Login
    //             </Button>
    //           </>
    //         ) : (
    //           renderForgotFlow()
    //         )}
    //       </Grid>
    //     </Grid>
    //   </Box>
    // </Box>
    <Box
      sx={{
        height: { xs: "auto", md: "100vh" },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        py: { xs: 4, md: 0 }, // padding on top/bottom for mobile
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* <Snackbar
        open={snackBar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"top" + "right"}
        autoHideDuration={6000}
        onClose={() => setSnackBar(false)}
      >
        <Alert
          onClose={() => setSnackBar(false)}
          severity={alertSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar> */}
      <ToastContainer position="top-right" />
      <Box
        sx={{
          width: { xs: "95%", md: "80%" }, // full width on mobile
          height: { xs: "auto", md: "80%" }, // auto height on mobile
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: 4,
          backgroundColor: "#fff",
          padding: { xs: 2, md: 4 }, // reduce padding on mobile
        }}
      >
        <Grid container sx={{ height: { xs: "auto", md: "100%" } }}>
          {/* Left Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "transparent",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              borderRadius: 2,
              backgroundImage: `url(${loginBgFirst})`,
              backgroundSize: "cover",
              backgroundPosition: "center 20%",
              height: { xs: "200px", md: "100%" }, // smaller height for mobile
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                alignItems: "center",
                justifyContent: "flex-start",
                lineHeight: 1.3,
                p: { xs: 2, md: 3 },
                fontSize: { xs: "20px", md: "h4.fontSize" }, // responsive font size
              }}
            >
              Your One Stop <br /> Procurement Solution
            </Typography>
          </Grid>

          {/* Right Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              p: { xs: 2, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              backgroundColor: "#fff",
            }}
          >
            {/* Logo */}
            {/* Logo */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 10, md: 20 }, // smaller top offset on mobile
                right: { xs: 10, md: 20 }, // smaller right offset on mobile
                width: { xs: 100, md: 140 }, // responsive width
              }}
            >
              <img
                src="https://360.procurementresource.com/assets/images/prlogo.webp"
                alt="Logo"
                style={{ width: "100%", height: "auto" }} // scales with box
              />
            </Box>

            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: "'Lora', serif",
                fontWeight: 600,
                fontSize: { xs: "28px", md: "35px" },
                lineHeight: 1.3,
              }}
            >
              {mode === "login" ? "Get Started" : "Reset Password"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: "text.secondary",
              }}
              mb={3}
            >
              {mode === "login"
                ? "Welcome to Sourcing Compass"
                : step === 1
                ? "Enter your email to receive OTP"
                : step === 2
                ? "Enter OTP sent to your email"
                : "Create a new password"}
            </Typography>

            {mode === "login" ? (
              <>
                <TextField
                  placeholder="Your email"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  placeholder="Your password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ background: "#e8f0fe" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Link
                  component="button"
                  variant="body2"
                  sx={{ alignSelf: "flex-end", color: "red", mb: 2 }}
                  onClick={() => setMode("forgot")}
                >
                  Forget password
                </Link>
                <Button
                  variant="contained"
                  fullWidth
                  sx={btnStyle}
                  onClick={handleLogin}
                >
                  Login
                </Button>
              </>
            ) : (
              renderForgotFlow()
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Login;
